import React, { useState, useCallback } from "react";
import { getProductLinkedLogs } from "../../../../hooks/api/apiDebug";
import DataProductLinkedLog from "./DataProductLinkedLog";
import './apiDebug.css';

const METHOD_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'linkProducts', label: 'linkProducts (Sync)' },
  { value: 'productHelper.deleteFromLinkedToList', label: 'deleteFromLinkedToList (Desync)' },
];

const BOOLEAN_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Sí' },
  { value: 'false', label: 'No' },
];

const ERROR_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Con error' },
  { value: 'false', label: 'Sin error' },
];

const INITIAL_FILTERS = {
  productId: '',
  requestedBy: '',
  method: '',
  massive: '',
  hasError: '',
  dateFrom: '',
  dateTo: '',
};

const PAGE_LIMIT = 10;

function toISOSafe(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
}

function buildRequestParams(filters, page, limit) {
  const params = { page, limit };

  if (filters.productId.trim()) params.productId = filters.productId.trim();
  if (filters.requestedBy.trim()) params.requestedBy = filters.requestedBy.trim();
  if (filters.method) params.method = filters.method;
  if (filters.massive !== '') params.massive = filters.massive;
  if (filters.hasError !== '') params.hasError = filters.hasError;

  const isoFrom = toISOSafe(filters.dateFrom);
  const isoTo = toISOSafe(filters.dateTo);
  if (isoFrom) params.dateFrom = isoFrom;
  if (isoTo) params.dateTo = isoTo;

  return params;
}

function FilterInput({ id, label, value, onChange, onKeyDown, type = "text", placeholder = "", disabled = false }) {
  return (
    <div className="search-input-group">
      <label htmlFor={id} className="search-label">{label}</label>
      <input
        id={id}
        className="search-input"
        type={type}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        style={{ width: '220px' }}
      />
    </div>
  );
}

function FilterSelect({ id, label, value, onChange, options, disabled = false }) {
  return (
    <div className="search-input-group">
      <label htmlFor={id} className="search-label">{label}</label>
      <select
        id={id}
        className="search-input"
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={{ width: '220px' }}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export default function ViewProductLinkedLogs() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setError(null);
    setData([]);

    try {
      const params = buildRequestParams(filters, targetPage, PAGE_LIMIT);
      const response = await getProductLinkedLogs(params);
      const result = response.data?.data || response.data;

      const records = result?.data || [];
      const pagination = result?.pagination || {};

      setData(records);
      setTotalRecords(pagination.total || 0);
      setTotalPages(pagination.totalPages || 0);

      if (records.length === 0) {
        setError("No se encontraron registros con los filtros aplicados");
      }
    } catch (err) {
      console.error("Error fetching product linked logs:", err);
      setError(err.response?.data?.message || "Error al obtener los datos.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleSearch = useCallback(() => {
    setPage(1);
    fetchData(1);
  }, [fetchData]);

  const handlePageChange = (direction) => {
    const newPage = direction === "back"
      ? Math.max(1, page - 1)
      : Math.min(totalPages, page + 1);

    setPage(newPage);
    fetchData(newPage);
  };

  const handleFilterChange = (field) => (e) => {
    const { value } = e.target;
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setData([]);
    setTotalRecords(0);
    setTotalPages(0);
    setError(null);
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="cron-debug-container">
      <div className="cron-debug-header">
        <h1 className="cron-debug-title">
          Logs de <strong>Sincronización / Desincronización</strong>
        </h1>
        <p className="cron-debug-subtitle">
          Historial de vinculación y desvinculación de productos (<strong>h_products_linked_to</strong>)
        </p>
      </div>

      <div className="cron-debug-search-section">
        <div className="search-container">
          <FilterInput
            id="productId"
            label="Product ID"
            value={filters.productId}
            onChange={handleFilterChange('productId')}
            onKeyDown={handleTextKeyDown}
            placeholder="ObjectId del producto"
            disabled={loading}
          />
          <FilterInput
            id="requestedBy"
            label="Requested By (User ID)"
            value={filters.requestedBy}
            onChange={handleFilterChange('requestedBy')}
            onKeyDown={handleTextKeyDown}
            placeholder="ObjectId del usuario"
            disabled={loading}
          />
          <FilterSelect
            id="method"
            label="Método"
            value={filters.method}
            onChange={handleFilterChange('method')}
            options={METHOD_OPTIONS}
            disabled={loading}
          />
          <FilterSelect
            id="massive"
            label="Masivo"
            value={filters.massive}
            onChange={handleFilterChange('massive')}
            options={BOOLEAN_OPTIONS}
            disabled={loading}
          />
          <FilterSelect
            id="hasError"
            label="Error"
            value={filters.hasError}
            onChange={handleFilterChange('hasError')}
            options={ERROR_OPTIONS}
            disabled={loading}
          />
          <FilterInput
            id="dateFrom"
            label="Desde"
            type="datetime-local"
            value={filters.dateFrom}
            onChange={handleFilterChange('dateFrom')}
            disabled={loading}
          />
          <FilterInput
            id="dateTo"
            label="Hasta"
            type="datetime-local"
            value={filters.dateTo}
            onChange={handleFilterChange('dateTo')}
            disabled={loading}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
          <button
            className="search-button"
            onClick={handleClearFilters}
            disabled={loading}
            style={{ backgroundColor: '#6c757d' }}
          >
            Limpiar
          </button>
        </div>

        {totalRecords > 0 && (
          <div className="pagination-info">
            <span>
              Mostrando página <strong>{page}</strong> de <strong>{totalPages}</strong>
            </span>
            <span className="total-registros">
              Total: <strong>{totalRecords}</strong> registro{totalRecords !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange("back")}
              disabled={page === 1 || loading}
            >
              ← Anterior
            </button>
            <span className="pagination-indicator">
              {page} / {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange("next")}
              disabled={page >= totalPages || loading}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="cron-debug-results">
        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p className="loading-text">Cargando registros...</p>
          </div>
        ) : data.length > 0 ? (
          <div className="results-list">
            {data.map((item, index) => (
              <DataProductLinkedLog
                key={item._id || index}
                data={item}
                index={index + (page - 1) * PAGE_LIMIT}
              />
            ))}
          </div>
        ) : !error ? (
          <div className="no-results">
            <p>Utiliza los filtros de arriba para buscar registros de sincronización</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
